// The catch form: photos, fish or game, species, size, water temperature, time, place on a small map, notes.
import type { Catch, Kind, Loc } from "../shared/model.ts";
import { dbDel, dbPut } from "./db.ts";
import { $, $maybe, el, icon, input, toast } from "./dom.ts";
import { LOCALE, fmtNum, methodLabel, t } from "./i18n.ts";
import { makeExpandable, collapseMini } from "./map/expand.ts";
import { FINLAND, hasLeaflet, lastLoc, layerName, tileLayer, withDepth, type CentredMap } from "./map/layers.ts";
import { dropPhotoUrls, photoUrl, readExif, shrink } from "./photos.ts";
import { METHODS, matchSpecies, speciesName, speciesOptions } from "./species.ts";
import { catches, render } from "./state.ts";
import { errorName, lsGet, lsSet, num, toLocalInput, uid } from "./util.ts";
import { fetchWaterTemp, waterSource } from "./water.ts";
import { needsWeather, refreshWeather } from "./weather.ts";
import { closeDetail, openDetail } from "./detail.ts";
import { combo } from "./combo.ts";

const dlg = $<HTMLDialogElement>("#edit");
interface NewPhoto { full: Blob; thumb: Blob; url: string }
let draft: Catch = blankCatch(), isNew = false;
let newPhotos: NewPhoto[] = [], removedPhotos: string[] = [];
let miniMap: CentredMap | null = null, miniMarker: L.Marker | null = null, locWatch = 0;

function blankCatch(): Catch {
  const kind: Kind = lsGet("kind", "fish") === "game" ? "game" : "fish";
  return { id: uid(), kind, species: "", time: Date.now(), weightKg: null, lengthCm: null,
    method: "", gear: "", released: false, place: "", notes: "", loc: null, weather: null, photoIds: [], createdAt: Date.now() };
}

/* ---------- fish or game, method ---------- */
function setKind(k: Kind): void {
  draft.kind = k;
  document.querySelectorAll<HTMLElement>("#e-kind button").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.kind === k)));
  $("#e-length-wrap").hidden = k !== "fish";
  $("#e-released-wrap").hidden = k !== "fish";
  $("#e-water-wrap").hidden = k !== "fish";
  previewWater();
  $("#e-gear-lbl").textContent = t(k === "fish" ? "gearFish" : "gearGame");
  input("#e-gear").placeholder = t(k === "fish" ? "gearFishPh" : "gearGamePh");
  input("#e-species").placeholder = t(k === "fish" ? "speciesFishPh" : "speciesGamePh");
  input("#e-place").placeholder = t(k === "fish" ? "placeFishPh" : "placeGamePh");
  $<HTMLTextAreaElement>("#e-notes").placeholder = t(k === "fish" ? "notesFishPh" : "notesGamePh");
  if (draft.method && !METHODS[k].includes(draft.method)) draft.method = "";
  drawMethods();
  speciesBox.close();
}
function drawMethods(): void {
  $("#e-method").replaceChildren(...METHODS[draft.kind].map((m) => el("button", { class: "chip", type: "button", "aria-pressed": String(draft.method === m),
    onclick: () => { draft.method = draft.method === m ? "" : m; drawMethods(); } }, methodLabel(m))));
}
document.querySelectorAll<HTMLElement>("#e-kind button").forEach((b) => b.addEventListener("click", () => setKind(b.dataset.kind === "game" ? "game" : "fish")));

/* ---------- species: type a name or pick one from the list (any other name is saved as typed) ---------- */
const speciesBox = combo({
  input: input("#e-species"),
  button: $<HTMLButtonElement>("#e-species-btn"),
  list: $<HTMLUListElement>("#e-species-list"),
  emptyText: () => t("speciesNoMatch"),
  options: (query) => speciesOptions(draft.kind, catches, LOCALE, { recent: t("speciesRecent"), all: t("speciesAll") })(query),
});

/* ---------- photos ---------- */
function drawPhotos(): void {
  const removeBtn = (onclick: () => void) => el("button", { type: "button", "aria-label": t("removePhoto"), onclick }, "×");
  const tiles: HTMLElement[] = [];
  for (const id of draft.photoIds) {
    const img = el("img", { alt: t("photoAlt") });
    void photoUrl(id).then((u) => { if (u) img.src = u; });
    tiles.push(el("div", { class: "ph" }, img, removeBtn(() => { draft.photoIds = draft.photoIds.filter((x) => x !== id); removedPhotos.push(id); drawPhotos(); })));
  }
  for (const p of newPhotos) {
    tiles.push(el("div", { class: "ph" }, el("img", { alt: t("photoAlt"), src: p.url }),
      removeBtn(() => { newPhotos = newPhotos.filter((x) => x !== p); URL.revokeObjectURL(p.url); drawPhotos(); })));
  }
  const file = el("input", { type: "file", accept: "image/*", multiple: true, id: "e-file" });
  file.addEventListener("change", () => void addPhotos([...(file.files ?? [])]));
  tiles.push(el("label", { class: "addph" }, icon("camera"), draft.photoIds.length + newPhotos.length ? t("addMorePhotos") : t("takePhoto"), file));
  $("#e-photos").replaceChildren(...tiles);
}
async function addPhotos(files: File[]): Promise<void> {
  const hint = $("#e-photohint");
  const say = (text: string, warn = false) => { hint.hidden = false; hint.className = warn ? "hint warn" : "hint"; hint.textContent = text; };
  for (const f of files) {
    if (!f.type.startsWith("image/")) continue;
    say(t("photoPreparing"));
    try {
      const [full, thumb, exif] = await Promise.all([shrink(f, 1600, 0.82), shrink(f, 360, 0.75), readExif(f)]);
      newPhotos.push({ full, thumb, url: URL.createObjectURL(thumb) });
      hint.hidden = true;
      // A photo taken earlier (picked from the gallery): use its own time and place.
      if (exif?.time && Math.abs(Date.now() - exif.time) > 20 * 6e4 && isNew) {
        input("#e-time").value = toLocalInput(exif.time);
        let msg = t("photoTime");
        if (exif.loc) { stopWatch(); setLoc({ ...exif.loc, acc: null, src: "photo" }, true); msg = t("photoTimePlace"); }
        say(msg);
      }
    } catch (e) {
      console.warn(e);
      say(t("photoBad"), true);
    }
    drawPhotos();
  }
}

/* ---------- water temperature: looked up as soon as a fish has a place, shown in the form and saved with it ---------- */
// (your own reading in the field always wins; without a place the value is filled in after saving, as before)
let waterToken = 0, waterTimer = 0;
function previewWater(): void {
  clearTimeout(waterTimer);
  if (draft.kind !== "fish" || !draft.loc || draft.water?.src === "manual" || input("#e-water").value.trim()) return;
  waterTimer = window.setTimeout(async () => {
    const my = ++waterToken, loc = draft.loc;
    if (!loc || !navigator.onLine) return;
    const time = Date.parse(input("#e-time").value) || draft.time;
    $("#e-waterhint").textContent = t("waterFetching");
    try {
      const w = await fetchWaterTemp(loc.lat, loc.lon, time);
      if (my !== waterToken || draft.loc !== loc || draft.water?.src === "manual") return;
      if (w.value != null && w.src !== "none") {
        draft.water = w;
        input("#e-water").placeholder = fmtNum(w.value, 1);
        $("#e-waterhint").textContent = t("waterAutoPh", fmtNum(w.value, 1), waterSource(w));
      } else { input("#e-water").placeholder = ""; $("#e-waterhint").textContent = t("waterNoneNear"); }
    } catch { if (my === waterToken) $("#e-waterhint").textContent = t("waterPh"); }
  }, 600);
}
input("#e-time").addEventListener("change", () => { if (draft.water?.src !== "manual") delete draft.water; previewWater(); });

/* ---------- place ---------- */
function setLoc(loc: Loc | null, pan = false): void {
  draft.loc = loc;
  const hint = $("#e-lochint");
  if (!loc) { hint.textContent = t("locNone"); return; }
  const src = loc.src === "gps" ? (loc.acc != null ? t("locGpsAcc", loc.acc) : t("locGps"))
    : loc.src === "photo" ? t("locPhoto") : loc.src === "map" ? t("locMap") : t("locSaved");
  hint.textContent = `${src} · ${loc.lat.toFixed(5)}, ${loc.lon.toFixed(5)}`;
  const full = $maybe("#e-mapbox .fullhint");
  if (full) full.textContent = hint.textContent;
  if (miniMap) {
    const ll: L.LatLngTuple = [loc.lat, loc.lon];
    if (!miniMarker) {
      const mk = L.marker(ll, { draggable: true }).addTo(miniMap);
      mk.on("dragend", () => { const p = mk.getLatLng(); stopWatch(); setLoc({ lat: p.lat, lon: p.lng, acc: null, src: "map" }); });
      miniMarker = mk;
    } else miniMarker.setLatLng(ll);
    if (pan) miniMap.setView(ll, Math.max(miniMap.getZoom(), 14));
  }
  previewWater();
}
function stopWatch(): void {
  if (locWatch) { navigator.geolocation.clearWatch(locWatch); locWatch = 0; }
}
/** Follows the GPS until it is accurate to 15 m (or the user picks a place another way). */
function startWatch(): void {
  stopWatch();
  if (!navigator.geolocation) { setLoc(draft.loc); return; }
  $("#e-lochint").textContent = t("locFinding");
  let first = true;
  locWatch = navigator.geolocation.watchPosition((p) => {
    const acc = Math.round(p.coords.accuracy);
    if (draft.loc?.src === "gps" && draft.loc.acc != null && acc > draft.loc.acc) return; // keep the better fix
    setLoc({ lat: p.coords.latitude, lon: p.coords.longitude, acc, src: "gps" }, first);
    first = false;
    if (acc <= 15) stopWatch();
  }, () => {
    stopWatch();
    if (!draft.loc) $("#e-lochint").textContent = t("locOff");
  }, { enableHighAccuracy: true, timeout: 30000, maximumAge: 30000 });
}
$("#e-locate").addEventListener("click", startWatch);

function initMiniMap(): void {
  if (!hasLeaflet()) { $("#e-map").replaceChildren(el("div", { class: "hint", style: "padding:12px", text: t("miniMapOffline") })); return; }
  const last = lastLoc();
  const start: L.LatLngTuple = draft.loc ? [draft.loc.lat, draft.loc.lon] : last ?? FINLAND;
  if (!miniMap) {
    const map: CentredMap = L.map("e-map", { zoomControl: true, attributionControl: false });
    tileLayer(layerName).addTo(map);
    withDepth(map);
    map.on("click", (e: L.LeafletMouseEvent) => { stopWatch(); setLoc({ lat: e.latlng.lat, lon: e.latlng.lng, acc: null, src: "map" }); });
    map.centre = () => (draft.loc ? [draft.loc.lat, draft.loc.lon] : map.getCenter());
    makeExpandable($("#e-mapbox"), () => miniMap, () => $("#e-lochint").textContent ?? "");
    miniMap = map;
  }
  miniMarker?.remove();
  miniMarker = null;
  const map = miniMap;
  map.setView(start, draft.loc ? 14 : last ? 11 : 5);
  requestAnimationFrame(() => map.invalidateSize());
}

/* ---------- open, close, save ---------- */
const inputNum = (n: number | null | undefined) => (n == null ? "" : n.toLocaleString(LOCALE, { maximumFractionDigits: 3, useGrouping: false }));

/** Opens the form for a new catch (null) or to edit `c`. */
export function openEditor(c: Catch | null): void {
  isNew = !c; newPhotos = []; removedPhotos = [];
  draft = c ? structuredClone(c) : blankCatch();
  $("#edit-title").textContent = isNew ? t("newCatch") : t("editCatch");
  input("#e-species").value = c ? speciesName(c) : "";
  input("#e-weight").value = inputNum(draft.weightKg);
  input("#e-length").value = inputNum(draft.lengthCm);
  input("#e-time").value = toLocalInput(draft.time);
  input("#e-gear").value = draft.gear || "";
  input("#e-released").checked = !!draft.released;
  // only your own reading goes in the field; an automatic value is shown as the hint instead
  const w = draft.water;
  input("#e-water").value = w?.src === "manual" ? inputNum(w.value) : "";
  input("#e-water").placeholder = w && w.src !== "manual" && w.value != null ? fmtNum(w.value, 1) : "";
  $("#e-waterhint").textContent = w && w.src !== "manual" && w.value != null ? t("waterAutoPh", fmtNum(w.value, 1), waterSource(w)) : t("waterPh");
  input("#e-place").value = draft.place || "";
  $<HTMLTextAreaElement>("#e-notes").value = draft.notes || "";
  $("#e-photohint").hidden = true;
  setKind(draft.kind);
  drawPhotos();
  closeDetail();
  dlg.showModal();
  $("#edit-form .sheet-body").scrollTop = 0;
  initMiniMap();
  if (isNew) startWatch(); else setLoc(draft.loc, true);
}
function closeEditor(): void {
  collapseMini?.();
  stopWatch();
  for (const p of newPhotos) URL.revokeObjectURL(p.url);
  newPhotos = [];
  dlg.close();
}
$("#add-btn").addEventListener("click", () => openEditor(null));
$("#edit-close").addEventListener("click", closeEditor);
$("#edit-cancel").addEventListener("click", closeEditor);
dlg.addEventListener("cancel", (e) => { e.preventDefault(); if (collapseMini) collapseMini(); else closeEditor(); });

$("#edit-form").addEventListener("submit", async (e) => {
  e.preventDefault();
  const species = input("#e-species").value.trim();
  if (!species) { input("#e-species").focus(); toast(t("needSpecies")); return; }
  const ts = Date.parse(input("#e-time").value);
  const speciesId = matchSpecies(species, draft.kind);
  Object.assign(draft, {
    species, time: Number.isFinite(ts) ? ts : draft.time,
    weightKg: num(input("#e-weight").value), lengthCm: draft.kind === "fish" ? num(input("#e-length").value) : null,
    gear: input("#e-gear").value.trim(), released: draft.kind === "fish" && input("#e-released").checked,
    place: input("#e-place").value.trim(), notes: $<HTMLTextAreaElement>("#e-notes").value.trim(), updatedAt: Date.now(),
  });
  if (speciesId) draft.speciesId = speciesId; else delete draft.speciesId;
  const save = $<HTMLButtonElement>("#edit-save");
  save.disabled = true; save.textContent = t("saving");
  try {
    for (const p of newPhotos) {
      const id = uid();
      await dbPut("photos", { id, full: p.full, thumb: p.thumb });
      draft.photoIds.push(id);
    }
    for (const id of removedPhotos) { await dbDel("photos", id); dropPhotoUrls(id); }
    const waterValue = draft.kind === "fish" ? num(input("#e-water").value) : null;
    if (waterValue != null) { if (draft.water?.src !== "manual" || draft.water.value !== waterValue) draft.water = { value: waterValue, src: "manual" }; }
    else if (draft.kind !== "fish" || draft.water?.src === "manual") delete draft.water;
    if (needsWeather(draft)) draft.weather = { status: "pending" };
    if (!draft.loc) draft.weather = null;
    await dbPut("catches", draft);
    lsSet("kind", draft.kind);
    const i = catches.findIndex((x) => x.id === draft.id);
    if (i >= 0) catches[i] = draft; else catches.push(draft);
    navigator.storage?.persist?.().catch(() => {});
    const wasNew = isNew, id = draft.id;
    closeEditor();
    render();
    toast(wasNew ? t("saved") : t("changesSaved"));
    if (!wasNew) openDetail(id);
    void refreshWeather();
  } catch (err) {
    console.error(err);
    const name = errorName(err);
    toast(name === "QuotaExceededError" ? t("storageFull") : name === "TimeoutError" ? t("saveStuck") : t("saveFailed"));
  } finally { save.disabled = false; save.textContent = t("saveCatch"); }
});
